import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { Response } from '../../../../_shared/models/response.model';
import { DeleteWorkshopRequest, WorkshopDetailsRequest } from '../models/workshop-request.model';
import { AccountCurrencyResponse, CurrencyOption, UpsertWorkshopResponse, WorkshopLocationResponse, WorkshopsResponse, WorkshopDetailsResponse } from '../models/workshop-response.model';
import { toWorkshopApiPayload, workshopDetailsFromApiResponse, workshopLocationFromApiResponse } from '../helpers/workshop-api.helper';

@Injectable({ providedIn: 'root' })
export class WorkshopService {
  private http = inject(HttpClient);

  async getWorkshops(accountIds: number[], cancelSubject: Subject<void>): Promise<Response<WorkshopsResponse[]>> {
    return await firstValueFrom(
      this.http.post(`${environment.apiEndpoint}/maintenancesettings/workshops`, { accountIds }).pipe(takeUntil(cancelSubject))
    );
  }

  async getWorkshopLocations(accountIds: number[], cancelSubject: Subject<void>): Promise<Response<WorkshopLocationResponse[]>> {
    const response = await firstValueFrom(
      this.http.post(`${environment.apiEndpoint}/maintenancesettings/workshop-locations`, { accountIds }).pipe(takeUntil(cancelSubject))
    );

    return {
      data: workshopLocationFromApiResponse(response)
    }
  }

  async getWorkshopDetails(id: number, cancelSubject: Subject<void>): Promise<Response<WorkshopDetailsResponse>> {
    const response = await firstValueFrom(
      this.http.get(`${environment.apiEndpoint}/maintenancesettings/workshops/${id}`).pipe(takeUntil(cancelSubject))
    );

    return {
      data: workshopDetailsFromApiResponse(response)
    };
  }

  async saveWorkshop(request: WorkshopDetailsRequest): Promise<Response<UpsertWorkshopResponse>> {
    const payload = toWorkshopApiPayload(request);
    return await firstValueFrom(
      this.http.post(`${environment.apiEndpoint}/maintenancesettings/upsert-workshop`, payload)
    );
  }

  async deleteWorkshop(request: DeleteWorkshopRequest): Promise<Response<void>> {
    return firstValueFrom(
      this.http.delete(`${environment.apiEndpoint}/maintenancesettings/workshop`, { body: request })
    );
  }

  async getCurrencyOptions(cancelSubject: Subject<void>): Promise<Response<CurrencyOption[]>> {
    return await firstValueFrom(
      this.http.get<Response<CurrencyOption[]>>(`${environment.apiEndpoint}/currency/currency-options`).pipe(takeUntil(cancelSubject))
    );
  }

  async getAccountCurrency(accountId: number, cancelSubject: Subject<void>): Promise<Response<AccountCurrencyResponse>> {
    return await firstValueFrom(
      this.http.get<Response<AccountCurrencyResponse>>(`${environment.apiEndpoint}/maintenancesettings/account-currency/${accountId}`).pipe(takeUntil(cancelSubject))
    );
  }
}